import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  ReactNode,
} from 'react';

import { LOGOUT } from '#graphql/mutations/login';
import { ME } from '#graphql/queries/me';
import { useAppConfig } from '#providers/app-config-provider';
import { getApolloPersistStorageKey } from '#utils/apollo-persist-key';
import { resetAuthSessionRefreshCircuit } from '#utils/auth-refresh-circuit';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  userName?: string | null;
  settings?: {
    aiAssistEditChatModel?: string | null;
    colorMode?: string | null;
  } | null;
  effectiveAiAssistEditChatModel: string;
}

interface UserContextType {
  user: AuthUser | null;
  loading: boolean;
  /** Call after successful login so the app refetches the current user (cookie is already set). */
  refetchMe: () => void;
  /** Clear session: call logout mutation and clear local state. */
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/** Applies `me.settings.colorMode` to app UI state when the session user loads or updates. */
function ColorModeUserSync() {
  const { user } = useUser();
  const { setColorModeFromServer } = useAppConfig();
  const userId = user?.id;
  const serverColorMode = user?.settings?.colorMode ?? 'light';
  useEffect(() => {
    if (userId) {
      setColorModeFromServer(serverColorMode === 'dark' ? 'dark' : 'light');
    }
  }, [userId, serverColorMode, setColorModeFromServer]);
  return null;
}

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const { config } = useAppConfig();
  const client = useApolloClient();
  const { data, loading, refetch } = useQuery(ME, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore',
    context: { skipAuthRefresh: true },
  });
  const [logoutMutation] = useMutation(LOGOUT, { errorPolicy: 'ignore' });

  const user = data?.me ?? null;

  const refetchMe = useCallback(() => {
    void refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    await logoutMutation();
    resetAuthSessionRefreshCircuit();
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(
          getApolloPersistStorageKey(config.graphqlUrl),
        );
      }
    } catch {
      // ignore storage errors
    }
    await client.resetStore();
  }, [logoutMutation, client, config.graphqlUrl]);

  const value: UserContextType = {
    user,
    loading,
    refetchMe,
    logout,
  };

  return (
    <UserContext.Provider value={value}>
      <ColorModeUserSync />
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
}

/** By default, validates that the caller is in an authenticated session. */
export function useCurrentUser(): AuthUser;
export function useCurrentUser(required: true): AuthUser;
export function useCurrentUser(required: false): AuthUser | null;
export function useCurrentUser(required = true): AuthUser | null {
  const { user } = useUser();
  if (required != false && user == null) {
    throw new Error('useCurrentUser: no current user (required)');
  }

  return user;
}
